import React, { useCallback, useEffect, useState } from 'react'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../store/hooks'
import { MissingScopeAction } from '../../../../components/ScopedBlock/models'
import { ScopedBlock } from '../../../../components/ScopedBlock/ScopedBlock'
import { useGetPresetByIdQuery } from '../../../../store/ai/ai'
import { PresetConfigLoader } from '../../AIFeaturePresets/PresetConfig/Common/PresetConfigLoader'
import { DISCUSSION_TOPICS_PRESET_IDS } from '../../../MeetingSummary/CRM/PresetMappings'
import { env } from '../../../../common/util'
import { useInputParametersUpdateScope } from '../../../../components/InputParameterHierarchy/hooks'
import { InputParameterConfigForm } from '../../../../components/InputParameterHierarchy/InputParameterConfigForm'
import { Button } from '../../../../components/Button/Button'
import { useUnifiedGlobalFormPresetInputParameters } from '../../AIFeaturePresets/PresetConfig/hooks'
import { useGetAccountQuery } from '../../../../store/accountUserSettings/accountUserSettings'
import { SuccessToast } from '../../../../components/SuccessToast/SuccessToast'

export const ConversationInsightsSettings = () => {
  const accountId = useAppSelector(selectAccountId)

  const presetId = DISCUSSION_TOPICS_PRESET_IDS[env]

  const { data: presetResponse } = useGetPresetByIdQuery({
    accountId,
    presetId,
    includeRestricted: true,
  })

  const { data: accountResponse } = useGetAccountQuery({ accountId })

  const lockedIPHierarchyTypes = accountResponse?.data?.enterpriseConfig?.lockedIPHierarchyTypes ?? []
  const isLockedByEnterprise = lockedIPHierarchyTypes.some((entry) => entry.typeId === presetId)

  const { presetInputParameters, updateInputParameters, updateLoading } = useUnifiedGlobalFormPresetInputParameters(presetResponse?.data)

  const { hasAiFeatureUpdateScope } = useInputParametersUpdateScope()

  const [displayedInputParameters, setDisplayedInputParameters] = useState<Record<string, unknown> | null>(null)
  const [isChanged, setChanged] = useState<boolean>(false)
  const [showDownloadToast, setShowDownloadToast] = useState<boolean>(false)

  useEffect(() => {
    setDisplayedInputParameters(presetInputParameters ?? {})
  }, [presetInputParameters])

  const handleClickCancel = () => {
    setDisplayedInputParameters(presetInputParameters ?? {})
  }

  const handleClickSave = useCallback(async () => {
    if (!displayedInputParameters) {
      return
    }
    setChanged(false)
    await updateInputParameters(displayedInputParameters)
  }, [displayedInputParameters, updateInputParameters])

  const handleExportInsights = () => {
    const hasTopics =
      displayedInputParameters?.discussionTopics &&
      Array.isArray(displayedInputParameters?.discussionTopics) &&
      displayedInputParameters?.discussionTopics?.length > 0

    const inputParameters = hasTopics
      ? displayedInputParameters
      : {
          discussionTopics: [
            {
              topicName: 'Example Topic',
              topicDescription: '',
            },
          ],
        }

    const blob = new Blob(
      [
        JSON.stringify(
          {
            type: 'ai-feature-presets',
            typeId: presetId,
            inputParameters,
          },
          null,
          2,
        ),
      ],
      { type: 'application/json' },
    )
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'insights_settings.json'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
    setShowDownloadToast(true)
  }

  useEffect(() => {
    setChanged(JSON.stringify(displayedInputParameters) !== JSON.stringify(presetInputParameters))
  }, [displayedInputParameters, presetInputParameters])

  if (!presetResponse?.data || !presetInputParameters || !displayedInputParameters) {
    return <PresetConfigLoader />
  }

  return (
    <div className='d-flex flex-column zocks-gap-1'>
      <SuccessToast position={'bottom-center'} message={`Insights settings downloaded.`} show={showDownloadToast} onClose={() => setShowDownloadToast(false)} />
      <h4>Discussion Topics</h4>
      <div className='d-flex zocks-p-1.25 gap-3 align-items-center zocks-bg-color-primary-light zocks-border-0.5'>
        <i className='fs-5 bi bi-info-square zocks-text-primary' />
        <p className='d-flex flex-column paragraph-medium m-0'>
          <span className='zocks-text-gray-900'>These settings apply to all meetings in your account.</span>
        </p>
      </div>
      {isLockedByEnterprise ? (
        <div className='d-flex zocks-p-1.25 gap-3 align-items-center zocks-bg-color-primary-light zocks-border-0.5'>
          <i className='fs-5 bi bi-info-square zocks-text-primary' />
          <p className='d-flex flex-column paragraph-medium m-0'>
            <span className='zocks-text-gray-900'>Preset configurations are managed at the enterprise level.</span>
          </p>
        </div>
      ) : null}
      <div className='d-flex gap-3'>
        <ScopedBlock
          scope={{
            scope: 'account:update',
            superScope: ['account:admin'],
          }}
          missingScopeAction={MissingScopeAction.DISABLE}
        >
          <div className='d-flex flex-column gap-3 w-100'>
            <InputParameterConfigForm
              inputParametersSchema={presetResponse.data.inputParametersSchema}
              inputParameters={displayedInputParameters}
              showCustomizationSetting={false}
              customized={false}
              onChange={setDisplayedInputParameters}
              disabled={!hasAiFeatureUpdateScope || isLockedByEnterprise}
            />
            <div className='w-100 d-flex justify-content-between zocks-mt-3'>
              <div className='d-flex gap-2'>
                {hasAiFeatureUpdateScope && !isLockedByEnterprise && (
                  <>
                    <Button variant='primary' onClick={handleClickSave} disabled={!isChanged} loading={updateLoading}>
                      Save
                    </Button>
                    <Button variant='secondary' onClick={handleClickCancel} disabled={!isChanged} loading={updateLoading}>
                      Discard
                    </Button>
                  </>
                )}
              </div>
              {hasAiFeatureUpdateScope && (
                <Button variant='secondary' onClick={handleExportInsights}>
                  <i className='bi bi-download me-2' />
                  Export
                </Button>
              )}
            </div>
          </div>
        </ScopedBlock>
      </div>
    </div>
  )
}
